import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import ArrowBackIcon from "@material-ui/icons/ArrowBackIosOutlined";
import AddIcon from "@material-ui/icons/Add";
import ReportIcon from "@material-ui/icons/AssessmentOutlined";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import DatasetFieldsEditor, { FIELD_TYPES } from "./DatasetFieldsEditor";

const T = {
  bg: "#F6F7F9",
  panel: "#FFFFFF",
  line: "#E6E9ED",
  hover: "#FAFBFC",
  ink: "#1B222A",
  ink2: "#5B6470",
  ink3: "#929AA4",
};

const OPS = [
  { value: "=", label: "igual" },
  { value: "≠", label: "diferente" },
  { value: "contém", label: "contém" },
  { value: ">", label: "maior" },
  { value: "<", label: "menor" },
  { value: "vazio", label: "vazio" },
];

const useStyles = makeStyles(theme => ({
  page: { background: T.bg, minHeight: "100%" },
  wrap: { maxWidth: 1180, margin: "0 auto", padding: theme.spacing(3) },
  top: { display: "flex", alignItems: "center", gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  title: { fontFamily: theme.app.heading, fontSize: 22, fontWeight: 700, color: theme.app.ink },
  count: { fontSize: 13, color: T.ink3, marginLeft: 8 },
  grow: { flex: 1 },
  filterBar: { display: "flex", alignItems: "center", gap: 8, marginBottom: theme.spacing(1.5), flexWrap: "wrap" },
  tableWrap: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 12, overflow: "hidden" },
  th: { fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: T.ink3, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  td: { fontSize: 13.5, color: T.ink, whiteSpace: "nowrap", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" },
  row: { cursor: "pointer", "&:hover": { background: T.hover } },
  empty: { textAlign: "center", color: T.ink3, padding: theme.spacing(6, 2) },
  pager: { display: "flex", alignItems: "center", gap: theme.spacing(2), justifyContent: "flex-end", marginTop: theme.spacing(1.5) },
  dl: { display: "grid", gridTemplateColumns: "150px 1fr", rowGap: 8, columnGap: 12, fontSize: 13.5 },
  dt: { color: T.ink3 },
  dd: { color: T.ink, wordBreak: "break-word" },
  linkBox: { marginTop: theme.spacing(2), paddingTop: theme.spacing(2), borderTop: `1px solid ${T.line}`, fontSize: 13 },
}));

const PAGE_SIZE = 25;

const DatasetView = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [ds, setDs] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState({ campo: "createdAt", dir: "desc" });
  const [flt, setFlt] = useState({ campo: "", op: "=", valor: "" });
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [detail, setDetail] = useState(null); // registro aberto
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [newField, setNewField] = useState([{ name: "", label: "", type: "texto", options: "" }]);
  const [addRecOpen, setAddRecOpen] = useState(false);
  const [recValues, setRecValues] = useState({});
  const [recSaving, setRecSaving] = useState(false);

  const loadSchema = useCallback(async () => {
    try {
      const { data } = await api.get(`/datasets/${id}`);
      setDs(data.dataset);
    } catch (err) {
      toastError(err);
    }
  }, [id]);

  const loadRecords = useCallback(async () => {
    try {
      const { data } = await api.get(`/datasets/${id}/records`, {
        params: {
          filtros: JSON.stringify(appliedFilters),
          ordenacao: JSON.stringify([order]),
          page,
          pageSize: PAGE_SIZE,
        },
      });
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch (err) {
      toastError(err);
    }
  }, [id, appliedFilters, order, page]);

  useEffect(() => {
    loadSchema();
  }, [loadSchema]);
  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const fields = (ds && ds.fields) || [];
  const columns = [...fields.map(f => ({ key: f.name, label: f.label || f.name, json: true })), { key: "createdAt", label: "Criado em", json: false }];

  const sortBy = campo => {
    setPage(1);
    setOrder(o => (o.campo === campo ? { campo, dir: o.dir === "asc" ? "desc" : "asc" } : { campo, dir: "asc" }));
  };

  const applyFilter = () => {
    setPage(1);
    if (!flt.campo) {
      setAppliedFilters([]);
      return;
    }
    const f = { campo: flt.campo, op: flt.op };
    if (flt.op !== "vazio") f.valor = flt.valor;
    setAppliedFilters([f]);
  };

  const cellValue = (rec, col) => {
    const v = col.json ? (rec.data || {})[col.key] : rec[col.key];
    if (v == null || v === "") return "—";
    if (col.key === "createdAt") return format(new Date(v), "dd/MM/yyyy HH:mm");
    if (typeof v === "object") return JSON.stringify(v);
    if (typeof v === "boolean") return v ? "sim" : "não";
    return String(v);
  };

  const addField = async () => {
    const f = newField[0];
    if (!String(f.name || "").trim()) return;
    const field = { name: f.name.trim(), label: f.label || undefined, type: f.type };
    if (f.type === "opcao" && f.options) field.options = String(f.options).split(",").map(s => s.trim()).filter(Boolean);
    if (f.indexed) field.indexed = true;
    try {
      await api.patch(`/datasets/${id}`, { field });
      setAddFieldOpen(false);
      setNewField([{ name: "", label: "", type: "texto", options: "" }]);
      loadSchema();
    } catch (err) {
      toastError(err);
    }
  };

  const openAddRecord = () => {
    setRecValues({});
    setAddRecOpen(true);
  };
  const saveRecord = async () => {
    setRecSaving(true);
    try {
      await api.post(`/datasets/${id}/records`, { data: recValues });
      setAddRecOpen(false);
      setPage(1);
      loadRecords();
    } catch (err) {
      // erros de validação por campo vêm em err.response.data.details
      const details = err && err.response && err.response.data && err.response.data.details;
      if (details && details.length) {
        toastError({ response: { data: { error: details.map(d => `${d.campo}: ${d.motivo}`).join(" · ") } } });
      } else {
        toastError(err);
      }
    } finally {
      setRecSaving(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.top}>
          <IconButton size="small" onClick={() => history.push("/datasets")}><ArrowBackIcon fontSize="small" /></IconButton>
          <Typography className={classes.title}>{ds ? ds.name : "…"}</Typography>
          <span className={classes.count}>{total} registro(s)</span>
          <div className={classes.grow} />
          <Button size="small" startIcon={<ReportIcon />} style={{ textTransform: "none", marginRight: 8 }} onClick={() => history.push(`/datasets/${id}/reports`)}>
            Relatórios
          </Button>
          <Button size="small" style={{ textTransform: "none", marginRight: 8 }} onClick={() => setAddFieldOpen(true)}>
            Adicionar campo
          </Button>
          <Button size="small" variant="contained" color="primary" disableElevation startIcon={<AddIcon />} style={{ textTransform: "none" }} onClick={openAddRecord}>
            Adicionar registro
          </Button>
        </div>

        <div className={classes.filterBar}>
          <TextField select label="Campo" value={flt.campo} onChange={e => setFlt({ ...flt, campo: e.target.value })} variant="outlined" size="small" style={{ minWidth: 160 }}>
            <MenuItem value="">— nenhum —</MenuItem>
            {fields.map(f => <MenuItem key={f.name} value={f.name}>{f.label || f.name}</MenuItem>)}
            <MenuItem value="createdAt">Criado em</MenuItem>
            <MenuItem value="contactKey">Contato</MenuItem>
          </TextField>
          <TextField select label="Operador" value={flt.op} onChange={e => setFlt({ ...flt, op: e.target.value })} variant="outlined" size="small" style={{ minWidth: 130 }}>
            {OPS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {flt.op !== "vazio" && (
            <TextField label="Valor" value={flt.valor} onChange={e => setFlt({ ...flt, valor: e.target.value })} variant="outlined" size="small" onKeyDown={e => e.key === "Enter" && applyFilter()} />
          )}
          <Button size="small" variant="outlined" onClick={applyFilter} style={{ textTransform: "none" }}>Filtrar</Button>
          {appliedFilters.length > 0 && (
            <Button size="small" onClick={() => { setFlt({ campo: "", op: "=", valor: "" }); setAppliedFilters([]); setPage(1); }} style={{ textTransform: "none" }}>Limpar</Button>
          )}
        </div>

        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell key={col.key} className={classes.th} onClick={() => sortBy(col.key)}>
                    {col.label}{order.campo === col.key ? (order.dir === "asc" ? " ▲" : " ▼") : ""}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className={classes.empty}>Nenhum registro.</TableCell>
                </TableRow>
              ) : (
                records.map(rec => (
                  <TableRow key={rec.id} className={classes.row} onClick={() => setDetail(rec)}>
                    {columns.map(col => (
                      <TableCell key={col.key} className={classes.td}>{cellValue(rec, col)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {pages > 1 && (
          <div className={classes.pager}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
            <span style={{ fontSize: 13, color: T.ink2 }}>{page} / {pages}</span>
            <Button size="small" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
          </div>
        )}
      </div>

      {/* registro + vínculos */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Registro #{detail && detail.id}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <>
              <div className={classes.dl}>
                {fields.map(f => (
                  <React.Fragment key={f.name}>
                    <div className={classes.dt}>{f.label || f.name}</div>
                    <div className={classes.dd}>{cellValue(detail, { key: f.name, json: true })}</div>
                  </React.Fragment>
                ))}
              </div>
              <div className={classes.linkBox}>
                <div className={classes.dl}>
                  <div className={classes.dt}>Criado em</div>
                  <div className={classes.dd}>{detail.createdAt ? format(new Date(detail.createdAt), "dd/MM/yyyy HH:mm") : "—"}</div>
                  <div className={classes.dt}>Contato</div>
                  <div className={classes.dd}>{detail.contactKey || "—"}</div>
                  <div className={classes.dt}>Automação</div>
                  <div className={classes.dd}>
                    {detail.applicationId ? (
                      <Button size="small" style={{ textTransform: "none", padding: 0, minWidth: 0 }} onClick={() => history.push(`/applications/${detail.applicationId}`)}>
                        abrir automação #{detail.applicationId}
                      </Button>
                    ) : "—"}
                  </div>
                  <div className={classes.dt}>Execução (runId)</div>
                  <div className={classes.dd} style={{ fontFamily: "monospace", fontSize: 12 }}>{detail.runId || "—"}</div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* adicionar campo (aditivo) */}
      <Dialog open={addFieldOpen} onClose={() => setAddFieldOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar campo</DialogTitle>
        <DialogContent dividers>
          <DatasetFieldsEditor fields={newField} onChange={setNewField} types={FIELD_TYPES} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFieldOpen(false)}>Cancelar</Button>
          <Button onClick={addField} color="primary" variant="contained" disableElevation>Adicionar</Button>
        </DialogActions>
      </Dialog>

      {/* adicionar registro manual (form gerado do esquema) */}
      <Dialog open={addRecOpen} onClose={recSaving ? undefined : () => setAddRecOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar registro</DialogTitle>
        <DialogContent dividers>
          {fields.length === 0 && <Typography style={{ color: T.ink3 }}>Esta tabela ainda não tem campos.</Typography>}
          {fields.map(f => {
            const label = `${f.label || f.name}${f.type !== "texto" ? ` · ${f.type}` : ""}`;
            const setV = v => setRecValues(rv => ({ ...rv, [f.name]: v }));
            const val = recValues[f.name];
            if (f.type === "opcao") {
              return (
                <TextField key={f.name} select label={label} value={val || ""} onChange={e => setV(e.target.value)} fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }}>
                  <MenuItem value="">—</MenuItem>
                  {(f.options || []).map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              );
            }
            if (f.type === "booleano") {
              return (
                <TextField key={f.name} select label={label} value={val == null ? "" : String(val)} onChange={e => setV(e.target.value === "" ? "" : e.target.value === "true")} fullWidth variant="outlined" size="small" style={{ marginBottom: 12 }}>
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="true">sim</MenuItem>
                  <MenuItem value="false">não</MenuItem>
                </TextField>
              );
            }
            return (
              <TextField
                key={f.name}
                label={label}
                type={f.type === "numero" ? "number" : f.type === "data" ? "datetime-local" : "text"}
                InputLabelProps={f.type === "data" ? { shrink: true } : undefined}
                value={val || ""}
                onChange={e => setV(e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                style={{ marginBottom: 12 }}
              />
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRecOpen(false)} disabled={recSaving}>Cancelar</Button>
          <Button onClick={saveRecord} color="primary" variant="contained" disableElevation disabled={recSaving || fields.length === 0}>
            {recSaving ? "Salvando…" : "Salvar registro"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DatasetView;
